import dayjs from 'dayjs'

import { WEBSITE_URL } from 'app/utils/constants/env.constants'
import { LISTING_STATUS } from 'app/utils/constants/status.constants'

import { getListingMetaImages } from '../utils/meta.helpers'
import {
  getAdditionalProperties,
  getBrand,
  getMerchantReturnPolicy,
  getSellerDetails,
  getShippingDetails
} from '../utils/productSchema.helpers'

const availabilityStatus = {
  [LISTING_STATUS.available]: 'https://schema.org/InStock',
  [LISTING_STATUS.reserved]: 'https://schema.org/Reserved',
  [LISTING_STATUS.sold_out]: 'https://schema.org/SoldOut'
}

interface ProductSchemaProps {
  listing: ListingModel
}

const ProductSchema = ({ listing }: ProductSchemaProps) => {
  const isNewCondition = listing.condition === 'new_item'
  const availability =
    availabilityStatus[listing.status] ?? 'https://schema.org/InStock'
  const itemCondition = isNewCondition
    ? 'https://schema.org/NewCondition'
    : 'https://schema.org/UsedCondition'

  const additionalProperties = getAdditionalProperties(listing)
  const shippingDetails = getShippingDetails(listing?.shipping)
  const merchantReturnPolicy = getMerchantReturnPolicy(listing?.shipping)
  const brand = getBrand({
    makes: listing?.makes,
    categoryOptions: listing?.category_options
  })
  const listingImage = getListingMetaImages({
    attachments: listing?.attachments,
    width: 800
  })
  const { seller, offeredBy } = getSellerDetails(listing?.user)
  const priceValidUntil = dayjs().add(1, 'year').format('YYYY-MM-DD')
  const productIdentifier = listing?.product_identifier

  const productSchema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: listing.title,
    ...(!!brand && { brand }),
    ...(!!productIdentifier && {
      sku: productIdentifier,
      mpn: productIdentifier
    }),
    image: listingImage ?? listing.attachments[0].url,
    description: listing.description,
    category: `${listing.sub_category.parent?.name} > ${listing.sub_category.name}`,
    width: listing.shipping?.width ?? '',
    height: listing.shipping?.height ?? '',
    weight: listing.shipping?.weight ?? '',
    offers: {
      '@type': 'Offer',
      url: `${WEBSITE_URL}/listing/${listing.slug}`,
      priceCurrency: listing.currency.name.toUpperCase(),
      price: listing.actual_price,
      itemCondition,
      availability,
      shippingDetails,
      hasMerchantReturnPolicy: merchantReturnPolicy,
      priceValidUntil,
      seller,
      ...(!!offeredBy && { offeredBy })
    },
    additionalProperty: additionalProperties
  }

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(productSchema)
      }}
    />
  )
}

export default ProductSchema
